import React from 'react';
import PropTypes from 'prop-types';

/**
 * ProductCard Component
 * Displays a single product result with rank, SKU, confidence, image, and metadata
 */
const ProductCard = ({ rank, sku, confidence, category, metadata, imageUrl, onClick }) => {
  const [imageError, setImageError] = React.useState(false);
  const [imageLoading, setImageLoading] = React.useState(true);

  // Determine confidence color based on percentage
  const getConfidenceColor = (conf) => {
    if (conf >= 90) return '#10b981'; // green
    if (conf >= 70) return '#f59e0b'; // yellow/amber
    return '#ef4444'; // red
  };

  const confidenceColor = getConfidenceColor(confidence);

  // Styles
  const styles = {
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      padding: '0',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: '1px solid #e5e7eb',
      position: 'relative',
      overflow: 'hidden',
    },
    cardHover: {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
    },
    imageContainer: {
      width: '100%',
      height: '200px',
      backgroundColor: '#f3f4f6',
      position: 'relative',
      overflow: 'hidden',
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      backgroundColor: '#ffffff',
    },
    imagePlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '48px',
      color: '#9ca3af',
    },
    imageLoader: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f3f4f6',
    },
    spinner: {
      width: '32px',
      height: '32px',
      border: '3px solid #e5e7eb',
      borderTop: '3px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    cardContent: {
      padding: '20px',
    },
    rankBadge: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      backgroundColor: 'rgba(239, 68, 68, 0.9)',
      color: '#ffffff',
      borderRadius: '50%',
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '14px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
      zIndex: 1,
    },
    skuTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '16px',
      wordBreak: 'break-word',
    },
    confidenceSection: {
      marginBottom: '16px',
    },
    confidenceLabel: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    confidenceValue: {
      fontWeight: '600',
      color: confidenceColor,
      fontSize: '16px',
    },
    progressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: '#e5e7eb',
      borderRadius: '4px',
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      backgroundColor: confidenceColor,
      width: `${confidence}%`,
      transition: 'width 0.3s ease',
      borderRadius: '4px',
    },
    categoryTag: {
      display: 'inline-block',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: '500',
      marginBottom: '16px',
    },
    metadataSection: {
      borderTop: '1px solid #e5e7eb',
      paddingTop: '16px',
    },
    metadataTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#4b5563',
      marginBottom: '8px',
    },
    metadataList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    metadataItem: {
      fontSize: '13px',
      color: '#6b7280',
      marginBottom: '6px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    metadataKey: {
      fontWeight: '500',
      color: '#4b5563',
    },
    metadataValue: {
      color: '#6b7280',
      textAlign: 'right',
      marginLeft: '12px',
    },
  };

  const [isHovered, setIsHovered] = React.useState(false);

  // Add keyframes for spinner animation
  React.useEffect(() => {
    const styleSheet = document.styleSheets[0];
    const keyframes = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    try {
      styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
    } catch (e) {
      // Rule might already exist
    }
  }, []);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  return (
    <div
      style={{
        ...styles.card,
        ...(isHovered ? styles.cardHover : {}),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {/* Rank Badge */}
      <div style={styles.rankBadge}>#{rank}</div>

      {/* Product Image */}
      <div style={styles.imageContainer}>
        {imageLoading && !imageError && (
          <div style={styles.imageLoader}>
            <div style={styles.spinner} />
          </div>
        )}
        {imageError || !imageUrl ? (
          <div style={styles.imagePlaceholder}>{imageUrl}</div>
        ) : (
          <img
            src={imageUrl}
            alt={sku}
            style={{
              ...styles.image,
              display: imageLoading ? 'none' : 'block',
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
      </div>

      {/* Card Content */}
      <div style={styles.cardContent}>
        {/* SKU Title */}
        <h3 style={styles.skuTitle}>{sku}</h3>

      {/* Confidence Score */}
      <div style={styles.confidenceSection}>
        <div style={styles.confidenceLabel}>
          <span>Accuracy</span>
          <span style={styles.confidenceValue}>{confidence?.toFixed(1) || '0'}%</span>
        </div>
        <div style={styles.progressBar}>
          <div style={styles.progressFill} />
        </div>
      </div>

      {/* Category Tag */}
      {category && (
        <div style={styles.categoryTag}>
          {category}
        </div>
      )}

      {/* Metadata */}
      {metadata && Object.keys(metadata).length > 0 && (
        <div style={styles.metadataSection}>
          <div style={styles.metadataTitle}>Details</div>
          <ul style={styles.metadataList}>
            {Object.entries(metadata).map(([key, value]) => (
              <li key={key} style={styles.metadataItem}>
                <span style={styles.metadataKey}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}:
                </span>
                <span style={styles.metadataValue}>{value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      </div>
    </div>
  );
};

// PropTypes validation
ProductCard.propTypes = {
  rank: PropTypes.number.isRequired,
  sku: PropTypes.string.isRequired,
  confidence: PropTypes.number.isRequired,
  category: PropTypes.string,
  metadata: PropTypes.object,
  imageUrl: PropTypes.string,
  onClick: PropTypes.func,
};

ProductCard.defaultProps = {
  category: null,
  metadata: {},
  imageUrl: null,
  onClick: () => {},
};

export default ProductCard;
